import TurndownService from 'turndown';

// Copilot Studio / Bot Framework bots send message text as HTML (e.g. "<b>Install office</b>",
// "<br>", "<a href>"). Our chat renderers expect Markdown and escape raw HTML, so the tags would show
// literally. This converts bot HTML → Markdown with a mature library (turndown) instead of a hand-rolled
// parser. Plain text / existing Markdown passes through unchanged. No HTML is ever rendered directly
// (turndown emits Markdown, which our renderers then escape/format). Anchor hrefs are additionally
// restricted to absolute http(s) URLs (see the safeAnchor rule) so a javascript:/data: scheme in a bot
// link can never survive into the emitted Markdown.

let service: TurndownService | null = null;

function getService(): TurndownService {
  if (service) return service;
  service = new TurndownService({
    headingStyle: 'atx',
    bulletListMarker: '-',
    codeBlockStyle: 'fenced',
    emDelimiter: '*',
  });
  // Preserve hard line breaks the bot intends with <br>.
  service.addRule('lineBreak', {
    filter: 'br',
    replacement: () => '\n',
  });
  // Turndown would faithfully preserve any <a href> scheme in the emitted Markdown link. Only emit a
  // link for absolute http(s) URLs; for any other scheme (javascript:, data:, mailto:, relative, …)
  // drop the href and keep just the visible text.
  service.addRule('safeAnchor', {
    filter: 'a',
    replacement: (content, node) => {
      const safe = safeHttpUrl((node as HTMLElement).getAttribute('href'));
      return safe ? `[${content}](${safe})` : content;
    },
  });
  return service;
}

/** True when the text looks like it contains HTML markup or entities worth converting. */
function looksLikeHtml(text: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(text) || /&(?:[a-z]+|#\d+);/i.test(text);
}

/**
 * Convert a bot/agent message that may contain HTML into Markdown. Returns the input unchanged when it
 * has no HTML (the common case), so AI/human plain-text + Markdown messages are untouched.
 */
export function botTextToMarkdown(text: string | undefined | null): string {
  if (!text) return '';
  if (!looksLikeHtml(text)) return text;
  try {
    return getService().turndown(text);
  } catch {
    return text;
  }
}

/**
 * Returns the URL only when it parses as an absolute http(s) URL; otherwise null. Used to guard
 * server-supplied links (e.g. the post-chat survey URL) before rendering them in an <a href> so a
 * javascript:/data: scheme can never be injected.
 */
export function safeHttpUrl(url: string | undefined | null): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.toString() : null;
  } catch {
    return null;
  }
}
